import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import type { ProjectOwnerWithUser, SplitType } from "@/hooks/use-project-detail";
import {
  useUpdateProjectOwner,
  useAddSalesUnitOwners,
  useDeleteProjectOwner,
} from "@/hooks/use-project-owners";
import { useSalesUnits, type SalesUnit } from "@/hooks/use-sales-units";
import { calcCommissionSplits } from "@/lib/billing-calculations";
import { formatNOK } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { useSystemConfigNumber } from "@/hooks/use-system-config";
import { DEFAULT_PROVISION_RATE_PCT } from "@/lib/constants";

interface ProvisjonTabProps {
  projectId: string;
  owners: ProjectOwnerWithUser[];
  reellBf: number | null;
  readOnly?: boolean;
  // VIB-1120: når prosjektet er lukket er provisjonen låst — bruk frossen sats.
  commissionLockedAt?: string | null;
  commissionRatePct?: number | null;
}

export default function ProvisjonssplittTab({
  projectId,
  owners,
  reellBf,
  readOnly = false,
  commissionLockedAt = null,
  commissionRatePct = null,
}: ProvisjonTabProps) {
  const { data: salesUnits } = useSalesUnits();
  const updateMut = useUpdateProjectOwner();
  const addMut = useAddSalesUnitOwners();
  const deleteMut = useDeleteProjectOwner();

  // VIB-1146: salgsenheter som ennå ikke er på prosjektet (ekskludér de som
  // allerede har eiere her, via sales_unit_id).
  const presentUnitIds = new Set(
    owners.map((o) => o.sales_unit_id).filter((id): id is string => !!id),
  );
  const availableUnits = (salesUnits ?? []).filter(
    (u) => !presentUnitIds.has(u.id),
  );

  const { data: configRatePct } = useSystemConfigNumber("provision_rate");
  // Låst prosjekt → frossen sats; ellers live config-sats (fallback default).
  const provisionPct =
    commissionLockedAt != null
      ? commissionRatePct ?? DEFAULT_PROVISION_RATE_PCT
      : configRatePct ?? DEFAULT_PROVISION_RATE_PCT;

  const bfValue = Number(reellBf) || 0;
  const totalProvision = bfValue * (provisionPct / 100);

  const splits = calcCommissionSplits(
    bfValue,
    owners.map((o) => ({
      user_id: o.user_id,
      split_type: o.split_type,
      ownership_pct: o.ownership_pct,
      fixed_amount: o.fixed_amount,
    })),
    provisionPct / 100,
  );
  const splitByUser = new Map(splits.map((s) => [s.user_id, s]));

  const fixedTotal = owners
    .filter((o) => o.split_type === "fixed")
    .reduce((s, o) => s + (o.fixed_amount ?? 0), 0);
  const overshoot = fixedTotal > bfValue && bfValue > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Provisjonssplitt</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="text-xs text-muted-foreground">Reell GEEK</p>
            <p className="text-lg font-semibold">{formatNOK(bfValue)}</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="text-xs text-muted-foreground">Total provisjon ({provisionPct}%)</p>
            <p className="text-lg font-semibold">{formatNOK(Math.round(totalProvision))}</p>
          </div>
        </div>

        {overshoot && (
          <div className="mb-4 px-3 py-2 rounded-md border-[1.5px] border-foreground bg-[#FEF3C7] text-[11px] text-[#78350F]">
            Sum av fastbeløp ({formatNOK(fixedTotal)}) overstiger reell GEEK.
            Beløpene skaleres ned proporsjonalt til summen ikke overstiger GEEK.
          </div>
        )}

        {owners.length === 0 ? (
          <p className="text-sm text-muted-foreground mb-3">Ingen prosjekteiere registrert.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b [&>th]:py-2 [&>th]:px-2 [&>th]:font-medium [&>th]:text-muted-foreground [&>th]:text-xs [&>th]:uppercase">
                <th className="text-left">Selger</th>
                <th className="text-left w-32">Type</th>
                <th className="text-right w-32">GEEK-andel</th>
                <th className="text-right w-32">Provisjon</th>
                <th className="w-24" aria-label="Handlinger" />
              </tr>
            </thead>
            <tbody>
              {owners.map((o) => (
                <OwnerRow
                  key={o.user_id}
                  owner={o}
                  bfValue={bfValue}
                  result={splitByUser.get(o.user_id)}
                  readOnly={readOnly || updateMut.isPending}
                  onSave={(patch) =>
                    updateMut.mutate({
                      project_id: projectId,
                      user_id: o.user_id,
                      patch,
                    })
                  }
                  onDelete={() =>
                    deleteMut.mutate({ project_id: projectId, user_id: o.user_id })
                  }
                />
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2">
                <td className="py-2.5 px-2 font-medium">Totalt</td>
                <td />
                <td className="py-2.5 px-2 text-right tabular-nums font-medium">
                  {formatNOK(splits.reduce((s, r) => s + r.bf_share, 0))}
                </td>
                <td className="py-2.5 px-2 text-right tabular-nums font-semibold">
                  {formatNOK(Math.round(splits.reduce((s, r) => s + r.provision, 0)))}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}

        {!readOnly && (
          <div className="mt-3">
            <AddSalesUnitPicker
              units={availableUnits}
              onAdd={(unit) =>
                addMut.mutate({
                  project_id: projectId,
                  sales_unit_id: unit.id,
                  member_user_ids: (unit.members ?? []).map((m) => m.user_id),
                })
              }
              disabled={addMut.isPending}
            />
          </div>
        )}

        {bfValue <= 0 && (
          <p className="text-xs text-muted-foreground mt-3">
            Reell GEEK er ikke beregnet ennå. Provisjon beregnes når faktisk GEEK er tilgjengelig.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function OwnerRow({
  owner,
  bfValue,
  result,
  readOnly,
  onSave,
  onDelete,
}: {
  owner: ProjectOwnerWithUser;
  bfValue: number;
  result: ReturnType<typeof calcCommissionSplits>[number] | undefined;
  readOnly: boolean;
  onSave: (patch: {
    split_type?: SplitType;
    ownership_pct?: number | null;
    fixed_amount?: number | null;
  }) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    split_type: owner.split_type,
    ownership_pct: owner.ownership_pct ?? 0,
    fixed_amount: owner.fixed_amount ?? 0,
  });

  useEffect(() => {
    if (!editing) {
      setDraft({
        split_type: owner.split_type,
        ownership_pct: owner.ownership_pct ?? 0,
        fixed_amount: owner.fixed_amount ?? 0,
      });
    }
  }, [editing, owner.split_type, owner.ownership_pct, owner.fixed_amount]);

  const isFixed = (editing ? draft.split_type : owner.split_type) === "fixed";
  const isRest = !isFixed && !editing && bfValue > 0 && owner.ownership_pct !== null;

  const handleSave = () => {
    onSave({
      split_type: draft.split_type,
      ownership_pct: draft.split_type === "percent" ? Number(draft.ownership_pct) : null,
      fixed_amount: draft.split_type === "fixed" ? Number(draft.fixed_amount) : null,
    });
    setEditing(false);
  };

  return (
    <tr className="border-b border-border/40">
      <td className="py-2.5 px-2">
        <div className="flex items-center gap-2">
          {owner.user?.avatar_url ? (
            <img
              src={owner.user.avatar_url}
              className="w-6 h-6 rounded-full object-cover"
              alt=""
            />
          ) : (
            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[9px] font-medium flex items-center justify-center">
              {(owner.user?.full_name ?? "?")
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)}
            </span>
          )}
          <span className="font-medium">{owner.user?.full_name ?? "Ukjent"}</span>
        </div>
      </td>
      <td className="py-2.5 px-2">
        {editing ? (
          <select
            className="h-7 rounded border border-input bg-background px-1.5 text-xs"
            value={draft.split_type}
            onChange={(e) =>
              setDraft({ ...draft, split_type: e.target.value as SplitType })
            }
          >
            <option value="percent">Prosent</option>
            <option value="fixed">Fastbeløp</option>
          </select>
        ) : (
          <span
            className={cn(
              "text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider",
              owner.split_type === "fixed"
                ? "bg-primary/10 text-primary"
                : "bg-muted text-muted-foreground",
            )}
          >
            {owner.split_type === "fixed" ? "Fast" : "Prosent"}
          </span>
        )}
      </td>
      <td className="py-2.5 px-2 text-right tabular-nums">
        {result ? formatNOK(Math.round(result.bf_share)) : "—"}
        {isRest && <span className="ml-1 text-[10px] text-muted-foreground">(rest)</span>}
      </td>
      <td className="py-2.5 px-2 text-right tabular-nums font-semibold">
        {result ? formatNOK(Math.round(result.provision)) : "—"}
      </td>
      <td className="py-2.5 px-2 w-24 text-right">
        {!readOnly &&
          (editing ? (
            <div className="inline-flex gap-0.5">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={handleSave}
                title="Lagre"
              >
                <SrIcon name="check" className="w-3.5 h-3.5 text-success" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={() => setEditing(false)}
                title="Avbryt"
              >
                <SrIcon name="x" className="w-3.5 h-3.5 text-muted-foreground" />
              </Button>
            </div>
          ) : (
            <div className="inline-flex gap-0.5">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-[11px]"
                onClick={() => setEditing(true)}
              >
                <SrIcon name="pencil" className="w-3 h-3 mr-1" />
                Endre
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-muted-foreground hover:text-destructive"
                onClick={() => {
                  if (confirm(`Fjerne ${owner.user?.full_name ?? "eier"} fra prosjektet?`)) {
                    onDelete();
                  }
                }}
                title="Fjern eier"
              >
                <SrIcon name="trash2" className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
      </td>
    </tr>
  );
}

function AddSalesUnitPicker({
  units,
  onAdd,
  disabled,
}: {
  units: SalesUnit[];
  onAdd: (unit: SalesUnit) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabled}>
          <SrIcon name="plus" className="w-3.5 h-3.5 mr-1.5" />
          Legg til selger
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start">
        <Command>
          <CommandInput placeholder="Søk i selgere..." className="h-9" />
          <CommandList className="max-h-72">
            <CommandEmpty>Ingen selgere matcher.</CommandEmpty>
            <CommandGroup heading={`${units.length} tilgjengelige`}>
              {units.map((u) => {
                const memberCount = u.members?.length ?? 0;
                return (
                  <CommandItem
                    key={u.id}
                    value={u.name}
                    onSelect={() => {
                      onAdd(u);
                      setOpen(false);
                    }}
                  >
                    <div className="flex flex-col">
                      <span className="text-xs font-medium">{u.name}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {memberCount} medlem{memberCount === 1 ? "" : "mer"}
                        {u.year ? ` · ${u.year}` : ""}
                      </span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
